"use client";

import { useState } from "react";
import Image from "next/image";
import { Star, Zap, Wrench, ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import CustomButton from "../ui/custom-button";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { SECTION_CONTAINER, SECTION_HEADING, SECTION_PADDING } from "@/lib/home-section-layout";

interface WhyUsFeature {
  id: string;
  icon: "star" | "zap" | "wrench";
  title: string;
  description: string[];
  image: string;
}

interface WhyUsSectionProps {
  className?: string;
}

const iconMap = { star: Star, zap: Zap, wrench: Wrench };

export function WhyUsSection({ className }: WhyUsSectionProps) {
  const t = useTranslations('whyUs');
  const features = t.raw('features') as WhyUsFeature[];

  const isMobileLandscape = useIsMobileLandscape();
  const isMobile = useIsMobile();
  const [activeFeature, setActiveFeature] = useState(0);
  const router = useRouter();

  const canGoPrev = activeFeature > 0;
  const canGoNext = activeFeature < features.length - 1;
  const currentFeature = features[activeFeature];
  const IconComponent = iconMap[currentFeature.icon];

  return (
    <section id="why-us" data-surface="base" className={cn(SECTION_PADDING, "relative bg-site", className)}>
      <div className={cn(SECTION_CONTAINER, isMobileLandscape && "!mx-6")}>
        <div className="grid grid-cols-1 font-outfit md:grid-cols-2 gap-6 md:gap-12 items-start">
          <div className="space-y-6">
            <div className="mb-8">
              <h2 className={SECTION_HEADING}>
                {t('sectionTitle')}
              </h2>
              <p className="text-white/90 text-lg">{t('sectionSubtitle')}</p>
            </div>

            <div className="bg-zinc-900/50 rounded-2xl p-6 md:p-8 border border-zinc-800 min-h-[300px] md:min-h-[350px] flex flex-col">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 bg-yellow-500/10 rounded-lg p-3">
                  <IconComponent className="h-6 w-6 text-yellow-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-yellow-500 leading-tight">
                  {currentFeature.title}
                </h3>
              </div>
              <div className="space-y-4 flex-grow">
                {currentFeature.description.map((paragraph, index) => (
                  <p key={index} className="text-white/90 leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <CustomButton variant="capsule" size={isMobile ? "md" : "lg"} onClick={() => router.push("/about/blog")}>
                {t('learnFromExperts')}
              </CustomButton>
              <div className="flex items-center gap-4">
                <button onClick={() => setActiveFeature(activeFeature - 1)} disabled={!canGoPrev} className={cn("rounded-full w-11 h-11 flex items-center justify-center text-black transition-colors", canGoPrev ? "bg-yellow-500 hover:bg-yellow-600" : "bg-yellow-500/30 cursor-not-allowed")} aria-label="Previous feature">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex">
                  {/* The dot stays 8px; the button around it is 32×44px so a
                      thumb can hit it. Touching buttons keep the dots on the
                      32px pitch the old 24px target plus 8px gap gave them. */}
                  {features.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setActiveFeature(index)}
                      aria-label={`View feature ${index + 1}`}
                      aria-current={index === activeFeature ? "true" : undefined}
                      className="group grid h-11 w-8 place-items-center focus-visible:outline-none"
                    >
                      <span
                        aria-hidden="true"
                        className={cn("block w-2 h-2 rounded-full transition-colors group-focus-visible:ring-2 group-focus-visible:ring-yellow-500 group-focus-visible:ring-offset-4 group-focus-visible:ring-offset-site", index === activeFeature ? "bg-yellow-500" : "bg-white/30")}
                      />
                    </button>
                  ))}
                </div>
                <button onClick={() => setActiveFeature(activeFeature + 1)} disabled={!canGoNext} className={cn("rounded-full w-11 h-11 flex items-center justify-center text-black transition-colors", canGoNext ? "bg-yellow-500 hover:bg-yellow-600" : "bg-yellow-500/30 cursor-not-allowed")} aria-label="Next feature">
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="relative order-first md:order-last">
            {/* w-full pins the width to the grid column. Without it the min-height
                wins over the aspect ratio, which then derives a width wider than
                the column and scrolls the page sideways at lg (visible at 1024px). */}
            <div className="w-full aspect-square md:aspect-[3/4] lg:aspect-[4/3] min-h-[300px] md:min-h-[400px] rounded-3xl overflow-hidden shadow-2xl">
              <Image src={currentFeature.image || "/placeholder.svg"} alt={currentFeature.title} fill className="object-cover transition-all duration-500 hover:scale-105 rounded-3xl" sizes="(max-width: 768px) 100vw, min(50vw, 880px)" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
